import { Injectable } from '@angular/core';
import { HttpSharpenService } from '@services/HttpServices/SharpenHttpService/http-sharpen.service';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { ISharpenModule, ISharpenPagedResponse, ISharpenTrack, ISharpenVideoData } from '@shared/models/sharpen-data-model';
import { BehaviorSubject, from, Observable, of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class SharpenService {
  isPostsLoadingSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  isPostsLoading$: Observable<boolean> = this.isPostsLoadingSubject.pipe(map(count => count > 0));
  isLoadingSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  isLoading$: Observable<boolean> = this.isLoadingSubject.pipe(map(count => count > 0));
  private readonly isNativePlatform = Capacitor.isNativePlatform();
  private readonly sharpenBaseUrl = environment.sharpenApi.replace(/\/+$/, '');

  constructor(private httpService: HttpSharpenService) {}

  getAllTracks(page?: number): Observable<ISharpenPagedResponse<ISharpenTrack> | undefined> {
    this.isLoadingSubject.next(this.isLoadingSubject.getValue() + 1);

    return this.runSharpenRequest(
      () => this.httpService.getAllTracks(page),
      () => this.nativeGet<ISharpenPagedResponse<ISharpenTrack>>('tracks', { page: `${page ?? 1}` })
    ).pipe(
      catchError(err => {
        console.log('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(this.isLoadingSubject.getValue() - 1)),
    );
  }

  getModulesByIds(ids: number[]): Observable<ISharpenPagedResponse<ISharpenModule> | undefined> {
    this.isLoadingSubject.next(this.isLoadingSubject.getValue() + 1);

    return this.runSharpenRequest(
      () => this.httpService.getModulesByIds(ids),
      () => this.nativeGet<ISharpenPagedResponse<ISharpenModule>>('modules', { module_ids: `${ids.join(',')}` })
    ).pipe(
      catchError(err => {
        console.log('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(this.isLoadingSubject.getValue() - 1)),
    );
  }

  getModuleById(id: string): Observable<ISharpenModule | undefined> {
    this.isLoadingSubject.next(this.isLoadingSubject.getValue() + 1);
    
    return this.runSharpenRequest(
      () => this.httpService.getModuleById(id),
      () => this.nativeGet<ISharpenModule>(`modules/${id}`)
    ).pipe(
      catchError(err => {
        console.log('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(this.isLoadingSubject.getValue() - 1)),
    );
  }

  getVideoById(id: number): Observable<ISharpenVideoData | undefined> {
    this.isLoadingSubject.next(this.isLoadingSubject.getValue() + 1);

    return this.runSharpenRequest(
      () => this.httpService.getVideoById(id),
      () => this.nativeGet<ISharpenVideoData>(`videos/${id}`)
    ).pipe(
      catchError(err => {
        console.log('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(this.isLoadingSubject.getValue() - 1)),
    );
  }

  trackModuleView(moduleTitle: string, userId: string) {
     this.isLoadingSubject.next(this.isLoadingSubject.getValue() + 1);

    return this.httpService.trackEvent(
      'Modules', 'View Module', moduleTitle, userId
    ).pipe(
      catchError(err => {
        console.log('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(this.isLoadingSubject.getValue() - 1)),
    );
  }

  private runSharpenRequest<T>(
    webRequestFactory: () => Observable<T>,
    nativeRequestFactory: () => Observable<T>
  ): Observable<T> {
    if (this.isNativePlatform) {
      return nativeRequestFactory();
    }
    
    return webRequestFactory();
  }

  private nativeGet<T>(path: string, params?: Record<string, string>): Observable<T> {
    return from(
      CapacitorHttp.get({
        url: `${this.sharpenBaseUrl}/${path}`,
        params,
        headers: {
          Authorization: `Api-Key ${environment.sharpenApiKey}`,
          Accept: 'application/json',
        },
      })
    ).pipe(
      map(response => {
        if (response.status >= 400) {
          throw response;
        }
  
        if (response.data?.detail) {
          throw response.data;
        }
  
        return response.data as T;
      })
    );
  }
}